import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface SessionMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export interface Session {
  sessionId: string;
  agentId: string;
  agentName: string;
  agentType: string;
  userId: string;
  organizationId?: string;
  userName?: string;
  userEmail?: string;
  organizationName?: string;
  status: 'success' | 'failed' | 'in-progress';
  summary: string;
  messageCount: number;
  totalDurationMs: number;
  delegationCount: number;
  createdAt: string;
  updatedAt?: string;
}

export interface ThinkingStep {
  id: string;
  type: 'routing' | 'context' | 'task_plan' | 'delegation' | 'reasoning';
  title: string;
  detail: string;
  timestamp: string;
}

export interface ActionEvent {
  id: string;
  type: 'api_call' | 'rag_query' | 'delegation' | 'task_execution' | 'error';
  title: string;
  detail: string;
  status: 'success' | 'failure';
  timestamp: string;
  duration?: number;
}

export interface SessionDetail extends Session {
  conversation: SessionMessage[];
  thinkingTrace: ThinkingStep[];
  actionsLog: ActionEvent[];
}

interface SessionsResponse {
  sessions: Session[];
  total: number;
  page: number;
  limit: number;
}

export const sessionsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSessions: builder.query<
      SessionsResponse,
      { page?: number; limit?: number; status?: string; agentId?: string; search?: string }
    >({
      query: (params) => ({
        url: '/sessions',
        params: {
          ...(params.page && { page: params.page }),
          ...(params.limit != null && { limit: params.limit }),
          ...(params.status && { status: params.status }),
          ...(params.agentId && { 'agent-id': params.agentId }),
          ...(params.search && { search: params.search }),
        },
      }),
      transformResponse: (raw: unknown) => normalizeKeys<SessionsResponse>(raw),
      providesTags: ['Session'],
    }),
    getSession: builder.query<SessionDetail, string>({
      query: (id) => `/sessions/${id}`,
      transformResponse: (raw: unknown) => normalizeKeys<SessionDetail>(raw),
      providesTags: (_result, _error, id) => [{ type: 'Session', id }],
    }),
    deleteSession: builder.mutation<void, string>({
      query: (id) => ({
        url: `/sessions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Session'],
    }),
  }),
});

export const {
  useGetSessionsQuery,
  useGetSessionQuery,
  useLazyGetSessionQuery,
  useDeleteSessionMutation,
} = sessionsApi;
